import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'

import {
  addGroupMember,
  assignGroupOfficer,
  changeGroupLeader,
  createGroup,
  exitGroupMember,
  recomputeGroupHealth,
  recordMeetingAttendance,
  scheduleGroupMeetings,
  setGroupMeetingLocation,
  updateGroup,
} from '@/api/groups-api'
import type {
  AddGroupMemberRequest,
  AssignGroupOfficerRequest,
  ChangeGroupLeaderRequest,
  ExitGroupMemberRequest,
  GroupUpdateRequest,
  GroupWriteRequest,
  MeetingLocationRequest,
  RecordAttendanceRequest,
  ScheduleMeetingsRequest,
} from '@/api/types'

import { groupKeys } from './group-keys'

/**
 * Commands over the group module.
 *
 * Every one of these changes something a list row shows — the member count, the leader, the
 * officer, the next meeting — so each invalidates the lists as well as the group it acted on.
 *
 * Health is invalidated alongside membership and attendance, and that is not housekeeping. The
 * attendance ratio is computed over member-meetings, so admitting a member, exiting one, or
 * recording a register all change the denominator the next score is calculated from. Leaving a
 * stale score on screen after one of them is how a group gets approved for a round on arithmetic
 * that no longer describes it.
 */
function invalidateGroup(queryClient: QueryClient, groupId: string) {
  void queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) })
  void queryClient.invalidateQueries({ queryKey: groupKeys.health(groupId) })
  void queryClient.invalidateQueries({ queryKey: groupKeys.lists() })
}

export function useCreateGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: GroupWriteRequest) => createGroup(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groupKeys.lists() })
    },
  })
}

export function useUpdateGroup(groupId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: GroupUpdateRequest) => updateGroup(groupId, request),
    onSuccess: (group) => {
      queryClient.setQueryData(groupKeys.detail(groupId), group)
      void queryClient.invalidateQueries({ queryKey: groupKeys.lists() })
    },
  })
}

export function useAddGroupMember(groupId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: AddGroupMemberRequest) => addGroupMember(groupId, request),
    onSuccess: (group) => {
      // The endpoint answers with the whole group, so the panel behind the dialog is correct the
      // moment it closes rather than one round trip later.
      queryClient.setQueryData(groupKeys.detail(groupId), group)
      void queryClient.invalidateQueries({ queryKey: groupKeys.health(groupId) })
      void queryClient.invalidateQueries({ queryKey: groupKeys.lists() })
    },
  })
}

export function useExitGroupMember(groupId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ customerId, ...request }: ExitGroupMemberRequest & { customerId: string }) =>
      exitGroupMember(groupId, customerId, request),
    onSuccess: (group) => {
      queryClient.setQueryData(groupKeys.detail(groupId), group)
      void queryClient.invalidateQueries({ queryKey: groupKeys.health(groupId) })
      void queryClient.invalidateQueries({ queryKey: groupKeys.lists() })
    },
  })
}

/**
 * Dual control. The answer is a `DualControlResult`, never the changed group, so nothing is
 * written into the cache here — when the request is awaiting a countersignature there is no
 * changed group to write. Invalidating is correct either way: it re-reads and finds either the new
 * leader or the old one, which is exactly the distinction the screen has to report.
 */
export function useChangeGroupLeader(groupId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ChangeGroupLeaderRequest) => changeGroupLeader(groupId, request),
    onSuccess: () => {
      invalidateGroup(queryClient, groupId)
    },
  })
}

/** Dual control — see {@link useChangeGroupLeader}. */
export function useAssignGroupOfficer(groupId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: AssignGroupOfficerRequest) => assignGroupOfficer(groupId, request),
    onSuccess: () => {
      invalidateGroup(queryClient, groupId)
    },
  })
}

/** Dual control — moving a pin changes where money may be collected. */
export function useSetGroupMeetingLocation(groupId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: MeetingLocationRequest) => setGroupMeetingLocation(groupId, request),
    onSuccess: () => {
      invalidateGroup(queryClient, groupId)
    },
  })
}

export function useScheduleGroupMeetings(groupId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ScheduleMeetingsRequest) => scheduleGroupMeetings(groupId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groupKeys.meetingLists(groupId) })
      invalidateGroup(queryClient, groupId)
    },
  })
}

export function useRecordMeetingAttendance(groupId: string, meetingId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: RecordAttendanceRequest) => recordMeetingAttendance(meetingId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groupKeys.meetingLists(groupId) })
      invalidateGroup(queryClient, groupId)
    },
  })
}

/**
 * Asks for the score to be brought up to date.
 *
 * The result is written straight into the health cache rather than invalidated: this is the one
 * call whose response *is* the new snapshot, and re-reading it would show the old figure for a
 * frame in the middle of the very act of refreshing it.
 */
export function useRecomputeGroupHealth(groupId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => recomputeGroupHealth(groupId),
    onSuccess: (health) => {
      queryClient.setQueryData(groupKeys.health(groupId), health)
      void queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) })
      void queryClient.invalidateQueries({ queryKey: groupKeys.lists() })
    },
  })
}
